import React, { useMemo, useEffect } from "react";
import { useSelector, shallowEqual } from "react-redux";
import { useProductSearch } from "hooks";
import { map, times, take } from "ramda";
import { AirbridgeCustomEvent, criteoEventPush, getDefaultPeriod } from "utils";
import {
  ProductCard,
  Pagination,
  SelectPeriod,
  RangePrice,
  SearchKeyword,
  DisplayCondition,
  SelectGroup,
  ProductsNotExist,
  SelectGroupWithBookmark,
  Loading
} from "components";
import { NEWEST } from "constant";
import { IStore } from "typings";
import classnames from "classnames/bind";
import styles from "components/pages/search/SearchShare.module.scss";

const cx = classnames.bind(styles);

export function NewestSearch() {
  const token = useSelector<IStore, IStore["member"]["token"]>(({ member: { token } }) => token);
  const genderId = useSelector<IStore, IStore["ui"]["genderId"]>(({ ui: { genderId } }) => genderId);
  const isMobile = useSelector<IStore, IStore["ui"]["isMobile"]>(({ ui: { isMobile } }) => isMobile, shallowEqual);
  const { email } = useSelector<IStore, IStore["member"]>(({ member }) => member);

  const base = useMemo(
    () => ({
      params: {
        order: NEWEST,
        page: { number: 1, size: isMobile ? 33 : 50 },
        period: getDefaultPeriod(),
        genderId
      }
    }),
    [genderId, isMobile]
  );

  const {
    handlePeriod,
    period,
    handlePrice,
    handlePriceReset,
    gte,
    lte,
    keyword,
    changeKeyword,
    handleKeyword,
    conditions,
    resetCondition,
    isLoading,
    products,
    reviews,
    totalCount,
    handlePage,
    number,
    size,
    maxPrice,
    group,
    handleGroup,
    mainRef
  } = useProductSearch(base);

  const COLUMN = useMemo(() => (isMobile ? 3 : 5), [isMobile]);

  const hiddenFlexBoxes = useMemo(
    () => times(v => v + 1, products.length % COLUMN ? COLUMN - (products.length % COLUMN) : 0),
    [COLUMN, products.length]
  );

  useEffect(() => {
    if (products.length > 0) {
      const productsItems = take(3, products).map(product => product.id);
      criteoEventPush({ email, event: "viewList", item: productsItems });
    }
  }, [products, email]);

  useEffect(() => {
    AirbridgeCustomEvent.mainScreen("home_newest");
  }, []);

  return (
    <main className={cx("main")} ref={mainRef}>
      <h2 className={cx("title")}>신상품</h2>

      {isLoading ? (
        <Loading />
      ) : (
        <>
          <div className={cx("filter-container")}>
            <ul className={cx("filter-list")}>
              <li className={cx("filter-item")}>
                <SelectPeriod handlePeriod={handlePeriod} period={period} />
              </li>

              <li className={cx("filter-item")}>
                {token ? (
                  <SelectGroupWithBookmark handleGroup={handleGroup} group={group} />
                ) : (
                  <SelectGroup handleGroup={handleGroup} group={group} />
                )}
              </li>

              <li className={cx("filter-item")}>
                <RangePrice
                  handlePrice={handlePrice}
                  handlePriceReset={handlePriceReset}
                  gte={gte}
                  lte={lte}
                  maxPrice={maxPrice}
                />
              </li>
            </ul>

            <SearchKeyword keyword={keyword} changeKeyword={changeKeyword} handleKeyword={handleKeyword} />
          </div>
          <DisplayCondition conditions={conditions} resetCondition={resetCondition} />

          <section className={cx("products")}>
            {products.length ? (
              <>
                {products.map((product, i) => (
                  <ProductCard product={product} review={reviews[i]} key={product.id} />
                ))}

                {map(
                  v => (
                    <div key={totalCount + v} className={cx("hidden-card")} />
                  ),
                  hiddenFlexBoxes
                )}
              </>
            ) : (
              <ProductsNotExist />
            )}
          </section>
        </>
      )}

      <Pagination totalCount={totalCount} handler={handlePage} number={number} size={size} />
    </main>
  );
}
